import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Cluster, ClusterNode, TimesheetRow } from '@sit/client-shared';
import type { ActivityItemType } from '@web/constants/dnd';
import { useCallback } from 'react';
import { ReduxStore } from '../types/redux/state';
import { useAppDispatch } from './store';

interface ReplaceTimeClockModalProps {
  currentTimeClockId: string;
  clockInTime: string;
  newTimeClockId: string;
}

interface AttachmentModalProps {
  timesheetId: string;
}

interface SplitTimeClockModalProps {
  timeClockId: string;
}

interface ClockOutViolationModalProps {
  timeClockId: string;
  onConfirm: (payload: { workLocationId: string; reason: string }) => void | Promise<void>;
}

interface AllocationModalProps {
  allocationId?: string;
}

interface OvertimeRuleModalProps {
  rulesetId?: string;
}

interface WorkLocationModalProps {
  workLocationId?: string;
}

interface ClusterAssignItem {
  data?: {
    node: ClusterNode | null;
    cluster: Cluster | null;
  };
  row?: TimesheetRow;
  type?: ActivityItemType;
}

export interface ClusterAssignValues {
  client?: string;
  project?: string;
  task?: string;
  item?: string;
  timeType?: string;
  costType?: string;
  employeePosition?: string;
  laborClass?: string;
  laborShift?: string;
  laborUnion?: string;
  department?: string;
  location?: string;
  billable?: boolean;
  rowId?: string;
}

export interface ClusterAssignModalProps {
  clusterId: string | undefined;
  item: ClusterAssignItem;
  values?: ClusterAssignValues | null;
}

interface LeavePolicyModalProps {
  policyId?: string;
}

interface ModalProps {
  attachments: AttachmentModalProps;
  replaceTimeClock: ReplaceTimeClockModalProps;
  splitTimeClock: SplitTimeClockModalProps;
  clockOutViolation: ClockOutViolationModalProps;
  allocation: AllocationModalProps;
  overtimeRuleset: OvertimeRuleModalProps;
  workLocation: WorkLocationModalProps;
  clusterAssign: ClusterAssignModalProps;
  leavePolicy: LeavePolicyModalProps;
  [key: `allocation-${string}`]: AllocationModalProps;
}

type ModalType = keyof ModalProps;

interface ModalState<T extends ModalType> {
  isOpen: boolean;
  modalType: T | null;
  modalProps: ModalProps[T] | null;
}

interface OpenModalPayload<T extends ModalType> {
  modalType: T;
  modalProps: ModalProps[T];
}

const initialState: ModalState<ModalType> = {
  isOpen: false,
  modalType: null,
  modalProps: null,
};

const modalSlice = createSlice({
  name: 'modal',
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<OpenModalPayload<ModalType>>) => {
      state.isOpen = true;
      state.modalType = action.payload.modalType;
      state.modalProps = action.payload.modalProps;
    },
    closeModal: (state) => {
      state.isOpen = false;
      state.modalType = null;
      state.modalProps = null;
    },
  },
});

export const selectIsModalOpen = (state: ReduxStore, modalType: ModalType) => state.modal.modalType === modalType && state.modal.isOpen;
export const selectIsAttachmentsModalOpen = (state: ReduxStore) => selectIsModalOpen(state, 'attachments');
export const selectIsReplaceTimeClockModalOpen = (state: ReduxStore) => selectIsModalOpen(state, 'replaceTimeClock');
export const selectIsSplitTimeClockModalOpen = (state: ReduxStore) => selectIsModalOpen(state, 'splitTimeClock');
export const selectIsClockOutViolationModalOpen = (state: ReduxStore) => selectIsModalOpen(state, 'clockOutViolation');
export const selectAllocationModalOpen = (state: ReduxStore, rowId?: string) =>
  selectIsModalOpen(state, rowId ? `allocation-${rowId}` : 'allocation');

export const selectModalProps = (state: ReduxStore) => state.modal.modalProps;
export const selectAttachmentModalProps = selectModalProps as (state: ReduxStore) => AttachmentModalProps;
export const selectReplaceTimeClockModalProps = selectModalProps as (state: ReduxStore) => ReplaceTimeClockModalProps;
export const selectSplitTimeClockModalProps = selectModalProps as (state: ReduxStore) => SplitTimeClockModalProps;
export const selectClockOutViolationModalProps = selectModalProps as (state: ReduxStore) => ClockOutViolationModalProps;
export const selectAllocationModalProps = selectModalProps as (state: ReduxStore) => AllocationModalProps;
export const selectOvertimeRulesetModalProps = selectModalProps as (state: ReduxStore) => OvertimeRuleModalProps | undefined;
export const selectWorkLocationModalProps = selectModalProps as (state: ReduxStore) => WorkLocationModalProps | undefined;
export const selectIsClusterAssignModalOpen = (state: ReduxStore) => selectIsModalOpen(state, 'clusterAssign');
export const selectClusterAssignModalProps = selectModalProps as (state: ReduxStore) => ClusterAssignModalProps | null;
export const selectLeavePolicyModalProps = selectModalProps as (state: ReduxStore) => LeavePolicyModalProps | undefined;

export const { openModal, closeModal } = modalSlice.actions;

export const useDispatchCloseModal = () => {
  const dispatch = useAppDispatch();
  return useCallback(() => dispatch(closeModal()), [dispatch]);
};

export const useDispatchOpenModal = () => {
  const dispatch = useAppDispatch();
  return useCallback(
    <T extends ModalType>(payload: OpenModalPayload<T>) => {
      dispatch(openModal(payload));
    },
    [dispatch],
  );
};

export const useModal = () => {
  const close = useDispatchCloseModal();
  const open = useDispatchOpenModal();
  return {
    close,
    open,
  };
};
export default modalSlice.reducer;
